import type { OfferView } from '@apm/contracts';
import { formatPrice } from '@apm/i18n';
import { useLocale, useT } from '../i18n/LocaleProvider.js';

/**
 * 상품 오버레이 (v23, `docs/47` §9.1).
 *
 * ── 규칙 넷 (절대 규칙 65) ─────────────────────────────────
 *
 * **① 카드 전체가 하나의 네이티브 `<button>` 이다.** `<div onClick>` 도 `<input>` 도
 * 아니다 — 포커스 후보가 `button:not([disabled]), a[href]` 뿐이라(`use-tv-focus.ts` 의
 * `FOCUSABLE`) 그 밖의 것은 **리모컨으로 닿지 않는데 마우스로는 멀쩡히 눌린다**
 * (규칙 29). **개발 기기에서는 영원히 드러나지 않고**, TV 앞에서는 「리모컨이 죽었다」와
 * 구분되지 않는다.
 *
 * **② `[닫기 ✕]` 는 카드 안의 둘째 `<button>` 이다.** 중첩 버튼이 되지 않게 카드는
 * `<button>` 과 `<button>` 을 나란히 둔 `<div>` 이고, **큰 쪽이 상품이다.**
 *
 * **③ 새 `data-focus-scope` 층을 만들지 않는다.** 기본 층에 버튼 둘이 늘 뿐이다 —
 * 만들면 `docs/25` §5 의 표와 K3 예외를 다시 봐야 하고, **그 층이 컨트롤 바를 가두면
 * 광고 중 구매 버튼에 리모컨이 닿지 않는다** (`docs/47` §14 의 4).
 *
 * **④ 컨트롤 바를 덮지 않는다.** 카드는 컨트롤 바 **위**에 앉고 폰 폭에서는 줄어든다 —
 * `getClientRects()` 가 겹치면 포커스 링의 기하 판정이 흔들린다.
 *
 * ── 포커스는 여기서 옮기지 않는다 (D10) ────────────────────
 *
 * **뜰 때 아무것도 하지 않는다.** 규칙 35 는 「선택 뒤 포커스는 구매 버튼으로 간다」인데
 * 그것은 **시청자가 고른 직후**다. 상품 카드는 시청자가 부르지 않았다 — 옮기면
 * **드라마를 보는 중에 리모컨이 자기 마음대로 움직인 것**이고, 마침 `[다음 회차 ▸]` 를
 * 누르려던 사람은 상품을 누르게 된다.
 *
 * **걷힐 때의 치유는 `WatchScreen` 이 한다** — 이 컴포넌트는 그때 이미 언마운트라
 * 스스로 할 수 없다.
 */
export function OfferCard({
  view,
  onPick,
  onDismiss,
}: {
  view: OfferView;
  /** 눌렀다 — 핸드오프가 시작된다 (`docs/47` §9.3) */
  onPick: () => void;
  /** 닫았다. **그 큐는 다시 열리지 않는다** */
  onDismiss: () => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const { item } = view;

  return (
    <div
      className="offer-card"
      data-testid="offer-card"
      data-offer-id={item.offerId}
      data-cue-id={view.cueId}
    >
      {/*
        **큰 쪽이 상품이다.** 카드 전체가 아니라 이 버튼이 카드의 몸통을 차지한다 —
        중첩 버튼을 만들지 않으면서 「카드를 누르면 산다」를 지키는 유일한 배치다.
      */}
      <button type="button" className="offer-card__main" onClick={onPick}>
        {/*
          **썸네일이 비어 있을 수 있다** — 브로커가 http 를 빈 문자열로 강등한다
          (`docs/47` §7.4 ①). 그때 자리표시자를 그린다: **상품명과 가격이 본체다.**
        */}
        {item.thumbnailUrl === '' ? (
          <div className="offer-card__thumb offer-card__thumb--empty" aria-hidden="true" />
        ) : (
          <img className="offer-card__thumb" src={item.thumbnailUrl} alt="" />
        )}

        <span className="offer-card__body">
          <span className="offer-card__badge">{t('offer.badge')}</span>
          {/*
            ★ **`data-i18n="data"` 가 필수다** — 상품명은 제휴사가 준 외부 데이터라
            사전을 거치지 않는다. 없으면 `verify-locale.mjs` 의 한글 훑기가 「번역 누락」
            으로 신고하고, **그 방향의 실패가 옳다**(절대 규칙 24).
          */}
          <span className="offer-card__title" data-i18n="data">
            {item.title}
          </span>
          <span className="offer-card__meta">
            {/* 가격이 0 이면 숨긴다 — 0 은 「모른다」의 값이다 (`docs/47` §7.4 ④) */}
            {item.price > 0 && (
              <span className="offer-card__price">{formatPrice(item.price, locale)}</span>
            )}
            <span className="offer-card__partner" data-i18n="data">
              {item.partnerName}
            </span>
          </span>
        </span>
      </button>

      {/*
        **둘째 버튼이다.** 리모컨으로 여기 닿을 수 있어야 「닫기」가 뜻을 갖는다 —
        `<span onClick>` 으로 두면 마우스에서만 눌린다.
      */}
      <button
        type="button"
        className="offer-card__close"
        data-testid="offer-close"
        aria-label={t('offer.close')}
        onClick={onDismiss}
      >
        ✕
      </button>
    </div>
  );
}
